import {getAnimationFrameScheduler} from '@frontapp/editor-html';
import {Trans} from '@lingui/macro';
import {range, startCase} from 'lodash';
import {DateTime, IANAZone, Interval} from 'luxon';
import React, {
  Dispatch,
  FC,
  Fragment,
  RefObject,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {EMPTY, fromEvent, interval} from 'rxjs';
import {distinctUntilChanged, map, mapTo, mergeMap, reduce, switchMap, windowTime} from 'rxjs/operators';
import styled from 'styled-components';

import {buildWindow} from '../../../../../libs/shared/helpers/src/browser/browserHelpers';
import {getWindow} from '../../../../../libs/shared/helpers/src/browser/domHelpers';
import {
  buildHoverParentClassName,
  hoverSelector,
} from '../../../../../libs/shared/helpers/src/browser/hoverHelpers';
import {useInitialLayoutEffect} from '../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {CalendarWeekdayToShortName, getSortedWeekdayNames} from '../../../../helpers/calendarHelpers';
import {
  build12Or24hOptionsForFormat,
  findTimeZone,
  TimeFormatsEnum,
} from '../../../../helpers/dateTimeHelpers';
import {buildIntervalFromRepeatingAvailabilityBlock} from '../../../../helpers/models/schedulingLinkAvailabilityHelpers';
import {CalendarWeekDaysEnum} from '../../../../models/api/calendarCommonTypes';
import {SchedulingLinkRepeatingAvailabilityBlockModel} from '../../../../models/api/schedulingLinkModel';
import {
  toggleRepeatingAvailabiltyBlock,
  toggleRepeatingAvailabiltyBlocks,
} from './recurringAvailabilityHelpers';

/*
 * Constants.
 */

// Distance (in px) from the edge of the screen while dragging until we start scrolling.
const scrollDragBoundary = 50;

const scrollSpeedFactor = 0.5;

/*
 * Types.
 */

interface RecurringAvailabilityCalendarProps {
  weekStart: CalendarWeekDaysEnum;
  selectedSlots: ReadonlyArray<SchedulingLinkRepeatingAvailabilityBlockModel>;
  setSelectedSlots: HandlerOf<ReadonlyArray<SchedulingLinkRepeatingAvailabilityBlockModel>>;
  timeFormat: TimeFormatsEnum;
}

interface CalendarDrag {
  start: SchedulingLinkRepeatingAvailabilityBlockModel;
  end: SchedulingLinkRepeatingAvailabilityBlockModel;
}

/*
 * Style.
 */

const StyledContentDiv = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  height: 100%;
  padding: 16px 0px;
  overflow: auto;
`;

const StyledHeaderDiv = styled.div`
  display: grid;
  grid-template-rows: auto;
  grid-template-columns: 50px repeat(7, 1fr);
  padding: 0px 26px 4px;
`;

const StyledGridDiv = styled.div`
  display: grid;
  grid-template-rows: auto;
  grid-template-columns: 50px repeat(7, 1fr);
  overflow: auto;

  position: relative;

  padding: 0px 26px;
`;

const StyledGridItemWrapperDiv = styled.div``;

const StyledDragOverlay = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
`;

interface StyledGridItemDivProps {
  isHighlighted?: boolean;
  isHoverable?: boolean;
}

const StyledGridItemDiv = styled.div<StyledGridItemDivProps>`
  height: 30px;
  background: ${(p) => (p.isHighlighted ? p.theme.palette.green.shade50 : p.theme.greys.shade20)};

  margin: 4px;
  border-radius: 4px;

  display: grid;
  align-items: center;
  justify-items: center;

  ${hoverSelector} {
    ${(p) => (p.isHoverable ? `background: ${p.theme.alphas.black30};` : '')};
  }
`;

const StyledIcon = styled(Icon)`
  display: none;

  color: ${(p) => p.theme.greys.shade50};
  pointer-events: none;

  ${hoverSelector} {
    display: block;
  }
`;

const StyledDivider = styled.div`
  grid-column: 1 / span 8;
  height: 1px;
  width: 100%;
  background-color: ${(p) => p.theme.greys.shade30};
`;

const StyledHourDiv = styled.div`
  text-align: right;
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.veryTiny};
  font-weight: ${fontWeights.medium};

  margin-top: 4px;
  margin-right: 10px;
`;

const StyledTimezoneDiv = styled(StyledHourDiv)`
  margin-top: 12px;
`;

const StyledWeekdayNameDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  margin: 9px 4px 8px;
`;

/*
 * Component.
 */

export const RecurringAvailabilityCalendar: FC<RecurringAvailabilityCalendarProps> = (props) => {
  const {selectedSlots} = props;
  const selectedIntervals = useMemo(() => buildIntervalsForSelectedSlots(selectedSlots), [selectedSlots]);
  const gridRef = useRef<HTMLDivElement>(null);
  const scrollStartRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<CalendarDrag>();
  const timezone = findTimeZone();

  useInitialLayoutEffect(() => {
    if (!scrollStartRef.current || !gridRef.current) {
      return;
    }

    gridRef.current.scrollTop = scrollStartRef.current.offsetTop;
  });

  const onCancelDrag = () => setDrag(undefined);
  const isDragging = drag !== undefined;

  useEffect(() => {
    if (!isDragging) {
      return undefined;
    }

    const grid = gridRef.current;
    if (!grid) {
      return undefined;
    }

    const window = getWindow(grid);
    if (!window) {
      return undefined;
    }

    const animationFrameScheduler = getAnimationFrameScheduler(buildWindow(window));
    const subscription = fromEvent<MouseEvent>(grid, 'mousemove')
      .pipe(
        map((event) => {
          const gridRect = grid.getBoundingClientRect();
          const distanceFromTop = event.clientY - gridRect.top;
          const distanceFromBottom = gridRect.bottom - event.clientY;
          if (distanceFromTop < scrollDragBoundary) {
            return (distanceFromTop - scrollDragBoundary) * scrollSpeedFactor;
          }
          if (distanceFromBottom < scrollDragBoundary) {
            return (scrollDragBoundary - distanceFromBottom) * scrollSpeedFactor;
          }
          return 0;
        }),
        distinctUntilChanged(),
        switchMap((scrollSpeed) => (scrollSpeed === 0 ? EMPTY : interval(16).pipe(mapTo(scrollSpeed)))),
        windowTime(0, animationFrameScheduler),
        mergeMap((offsets$) => offsets$.pipe(reduce((totalOffset, offset) => totalOffset + offset))),
      )
      .subscribe((scrollOffsetInThisAnimationFrame) => {
        grid.scrollTop += scrollOffsetInThisAnimationFrame;
      });

    return () => subscription.unsubscribe();
  }, [isDragging]);

  return (
    <>
      {drag && <StyledDragOverlay onMouseUp={onCancelDrag} />}
      <StyledContentDiv onMouseUp={onCancelDrag}>
        <StyledHeaderDiv>{renderHeader(props, timezone)}</StyledHeaderDiv>
        <StyledGridDiv ref={gridRef}>
          {range(0, 12).map((hour) => (
            <Fragment key={hour}>
              {renderHourRow(
                props,
                hour,
                timezone,
                selectedIntervals,
                hour === 8 ? scrollStartRef : undefined,
                drag,
                setDrag,
              )}
            </Fragment>
          ))}
          <StyledDivider />
          {range(12, 24).map((hour) => (
            <Fragment key={hour}>
              {renderHourRow(props, hour, timezone, selectedIntervals, undefined, drag, setDrag)}
            </Fragment>
          ))}
        </StyledGridDiv>
      </StyledContentDiv>
    </>
  );
};

/*
 * Helpers.
 */

function renderHeader(props: RecurringAvailabilityCalendarProps, timezone: string) {
  return (
    <>
      <StyledTimezoneDiv>
        {IANAZone.create(timezone).offsetName(Date.now(), {format: 'short'})}
      </StyledTimezoneDiv>
      {getSortedWeekdayNames(props.weekStart).map((weekday) => (
        <StyledWeekdayNameDiv key={weekday}>
          {startCase(CalendarWeekdayToShortName[weekday])}
        </StyledWeekdayNameDiv>
      ))}
    </>
  );
}

function renderHourRow(
  props: RecurringAvailabilityCalendarProps,
  hour: number,
  timezone: string,
  selectedIntervals: ReadonlyArray<Interval>,
  ref: RefObject<HTMLDivElement> | undefined,
  drag: CalendarDrag | undefined,
  setDrag: Dispatch<SetStateAction<CalendarDrag | undefined>>,
) {
  const {weekStart} = props;
  const durationMinutes = 30;
  return (
    <>
      <StyledHourDiv ref={ref}>
        {DateTime.local()
          .set({hour})
          .toLocaleString({
            hour: 'numeric',
            ...build12Or24hOptionsForFormat(props.timeFormat),
          })}
      </StyledHourDiv>
      {getSortedWeekdayNames(weekStart).map((weekDay) =>
        renderGridItem(
          props,
          {
            hour,
            minute: 0,
            weekDay,
            timezone,
            durationMinutes,
          },
          selectedIntervals,
          drag,
          setDrag,
        ),
      )}
      {/* Empty div since we don't need a label for the 30 minute mark. */}
      <div />
      {getSortedWeekdayNames(weekStart).map((weekDay) =>
        renderGridItem(
          props,
          {
            hour,
            minute: 30,
            weekDay,
            timezone,
            durationMinutes,
          },
          selectedIntervals,
          drag,
          setDrag,
        ),
      )}
    </>
  );
}

function renderGridItem(
  props: RecurringAvailabilityCalendarProps,
  slot: SchedulingLinkRepeatingAvailabilityBlockModel,
  selectedIntervals: ReadonlyArray<Interval>,
  drag: CalendarDrag | undefined,
  setDrag: Dispatch<SetStateAction<CalendarDrag | undefined>>,
) {
  const slotInterval = buildIntervalFromRepeatingAvailabilityBlock(slot);
  const isSelected = selectedIntervals.some((selectedInterval) => selectedInterval.overlaps(slotInterval));
  const isInDrag = isGridItemBeingDraggedOver(props, drag, slot);
  const onStartDrag = () => setDrag({start: slot, end: slot});
  const onUpdateDrag = () => {
    setDrag((previousDrag) => {
      if (!previousDrag) {
        // We aren't dragging.
        return undefined;
      }

      return {start: previousDrag.start, end: slot};
    });
  };
  const onEndDrag = () => {
    if (!isInDrag || !drag) {
      return;
    }

    const {start, end} = drag;
    if (start.hour === end.hour && start.minute === end.minute && start.weekDay === end.weekDay) {
      // Don't do anything if the start and end are the same. (This is essentially a click.)
      return;
    }

    const slotsToAdd = buildSlotsForDrag(props, drag);
    props.setSelectedSlots(toggleRepeatingAvailabiltyBlocks(props.selectedSlots, slotsToAdd));
  };

  const onClick = () => props.setSelectedSlots(toggleRepeatingAvailabiltyBlock(props.selectedSlots, slot));

  const isHighlighted = isSelected !== isInDrag;
  const shouldEnableHoverState = !isHighlighted && !drag;
  const renderTooltip = shouldEnableHoverState
    ? () => <Trans id="scheduling-calendar-hover">Click or drag to add available times.</Trans>
    : false;

  return (
    <StyledGridItemWrapperDiv
      key={`${slot.weekDay},${slot.hour},${slot.minute}`}
      onClick={onClick}
      className={buildHoverParentClassName()}
      onMouseDown={onStartDrag}
      onMouseEnter={onUpdateDrag}
      onMouseUp={onEndDrag}
    >
      <TooltipCoordinator render={renderTooltip}>
        <StyledGridItemDiv isHighlighted={isHighlighted} isHoverable={shouldEnableHoverState}>
          {shouldEnableHoverState && <StyledIcon name="plusCircleFilledSmall" />}
        </StyledGridItemDiv>
      </TooltipCoordinator>
    </StyledGridItemWrapperDiv>
  );
}

function buildIntervalsForSelectedSlots(slots: ReadonlyArray<SchedulingLinkRepeatingAvailabilityBlockModel>) {
  return slots.map(buildIntervalFromRepeatingAvailabilityBlock);
}

function isGridItemBeingDraggedOver(
  props: RecurringAvailabilityCalendarProps,
  drag: CalendarDrag | undefined,
  slot: SchedulingLinkRepeatingAvailabilityBlockModel,
) {
  if (!drag) {
    return false;
  }

  const {start, end} = drag;

  const weekDayNames = getSortedWeekdayNames(props.weekStart);
  const startWeekDayIndex = weekDayNames.indexOf(start.weekDay);
  const endWeekDayIndex = weekDayNames.indexOf(end.weekDay);
  const minWeekDayIndex = Math.min(startWeekDayIndex, endWeekDayIndex);
  const maxWeekDayIndex = Math.max(startWeekDayIndex, endWeekDayIndex);
  const slotWeekDayIndex = weekDayNames.indexOf(slot.weekDay);

  if (slotWeekDayIndex < minWeekDayIndex || slotWeekDayIndex > maxWeekDayIndex) {
    // Slot weekday is outside drag range.
    return false;
  }

  const startTime = start.hour * 60 + start.minute;
  const endTime = end.hour * 60 + end.minute;
  const minTime = Math.min(startTime, endTime);
  const maxTime = Math.max(startTime, endTime);
  const slotTime = slot.hour * 60 + slot.minute;
  return minTime <= slotTime && slotTime <= maxTime;
}

function buildSlotsForDrag(
  props: RecurringAvailabilityCalendarProps,
  drag: CalendarDrag,
): ReadonlyArray<SchedulingLinkRepeatingAvailabilityBlockModel> {
  const {start, end} = drag;

  const weekDayNames = getSortedWeekdayNames(props.weekStart);
  const startWeekDayIndex = weekDayNames.indexOf(start.weekDay);
  const endWeekDayIndex = weekDayNames.indexOf(end.weekDay);
  const minWeekDayIndex = Math.min(startWeekDayIndex, endWeekDayIndex);
  const maxWeekDayIndex = Math.max(startWeekDayIndex, endWeekDayIndex);

  const selectedWeekdays = weekDayNames.slice(minWeekDayIndex, maxWeekDayIndex + 1);

  const startTime = start.hour * 60 + start.minute;
  const endTime = end.hour * 60 + end.minute;
  const baseBlock = startTime < endTime ? start : end;
  const minTime = Math.min(startTime, endTime);
  const maxTime = Math.max(startTime, endTime);

  return selectedWeekdays.map((weekDay) => ({
    ...baseBlock,
    durationMinutes: maxTime - minTime + 30,
    weekDay,
  }));
}
